/**
 * Microphone permission hook.
 *
 * React hook for managing microphone permission state and requests.
 * Handles permission requests using navigator.mediaDevices.getUserMedia()
 * with appropriate audio constraints.
 *
 * @module useMicrophonePermission
 */

import { useState, useCallback, useEffect } from 'react';
import { logger } from '@/lib/logger';

export type MicrophonePermissionState = 'unknown' | 'granted' | 'denied';

/**
 * Hook for managing microphone permission
 *
 * Provides state management and request functionality for microphone access.
 * Uses getUserMedia API to request permission and tracks the permission state.
 *
 * @returns Object containing permission state and request function
 *
 * @example
 * ```typescript
 * const {
 *   permission,
 *   requestPermission,
 *   isSupported,
 * } = useMicrophonePermission();
 *
 * // Request permission
 * const granted = await requestPermission();
 * ```
 */
export function useMicrophonePermission() {
  const [permission, setPermission] =
    useState<MicrophonePermissionState>('unknown');

  const isSupported = typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    !!navigator.mediaDevices.getUserMedia;

  // Check existing browser permission state on mount so we don't show
  // "Permission will be requested" when the user already granted it.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.permissions) return;
    navigator.permissions.query({ name: 'microphone' as PermissionName }).then((status) => {
      if (status.state === 'granted') setPermission('granted');
      else if (status.state === 'denied') setPermission('denied');
      status.onchange = () => {
        if (status.state === 'granted') setPermission('granted');
        else if (status.state === 'denied') setPermission('denied');
        else setPermission('unknown');
      };
    }).catch(() => {/* Permissions API not supported — leave state as unknown */});
  }, []);

  /**
   * Request microphone permission
   *
   * Uses getUserMedia to request microphone access with audio constraints.
   * Stops the stream immediately after permission is granted to avoid
   * keeping the microphone active unnecessarily.
   *
   * @returns Promise resolving to true if permission granted, false otherwise
   */
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setPermission('denied');
      return false;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      // Permission granted - stop the stream immediately
      stream.getTracks().forEach((track) => track.stop());
      setPermission('granted');
      return true;
    } catch (error) {
      logger.error('Microphone permission denied', error);
      setPermission('denied');
      return false;
    }
  }, [isSupported]);

  /**
   * Reset permission state to unknown
   */
  const resetPermission = useCallback(() => {
    setPermission('unknown');
  }, []);

  return {
    permission,
    requestPermission,
    resetPermission,
    isSupported,
  };
}

